import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router";
import { DashboardLayout } from "@/components/layout/dashboard-layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Plus,
  Trash2,
  Save,
  Download,
  RefreshCw,
  Home,
  CheckCircle,
  CreditCard,
  Banknote,
  Smartphone,
  AlertCircle
} from "lucide-react";
import { generateSaleBill } from "@/utils/pdf-generator";
import { Sale, SaleItem, Stock } from "@/types/api";
import { saleService } from "@/services/api";
import { useToast } from "@/hooks/use-toast";

// Payment modes
type PaymentMode = "Cash" | "Card" | "UPI";

const SalePage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [stocks, setStocks] = useState<Stock[]>([]);
  const [customer, setCustomer] = useState("");
  const [saleDate, setSaleDate] = useState(new Date().toISOString().split('T')[0]);
  const [items, setItems] = useState<SaleItem[]>([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [paymentMode, setPaymentMode] = useState<PaymentMode>("Cash");
  const [lastSaleData, setLastSaleData] = useState<{
    customer: string;
    saleDate: string;
    items: SaleItem[];
    grandTotal: number;
    paymentMode: PaymentMode;
    invoiceNumber: string;
  } | null>(null);

  // Generate a new empty row
  const generateNewItem = (): SaleItem => ({
    id: Date.now().toString(),
    productId: 0,
    productName: "",
    quantity: 0,
    unitPrice: 0,
    tax: 0,
    discount: 0,
    total: 0,
    availableQty: 0,
    invoiceNumber: ""
  });

  // Use useEffect to fetch options on page load
  useEffect(() => {
    const fetchProducts = async () => {
      const stocks = await saleService.getStocks();
      if(stocks.length === 0) 
        toast({
          variant: "destructive",
          title: "Fetch Stocks",
          description: "No stocks available",
        });
        else
      setStocks(stocks);
    };

    fetchProducts();
  }, []); // Empty dependency array means it runs once on mount

  // Add a new empty row
  const handleAddItem = () => {
    setItems([...items, generateNewItem()]);
  };

  // Clear all items
  const handleClearItems = () => {
    setItems([]);
    setGrandTotal(0);
  };

  // Handle product selection
  const handleProductChange = (itemId: string, productId: number) => {
    if (items.find(i => i.productId === productId)) {
      toast({
        variant: "destructive",
        title: "Purchase Entry",
        description: "Product already selected"
      });
      return; // If the product is already selected, do nothing])
    }
    const selectedProduct = stocks.find(p => p.productId === productId);

    if (selectedProduct) {
      setItems(items.map(item => {
        if (item.id === itemId) {
          const updatedItem = {
            ...item,
            productId,
            productName: selectedProduct.productName,
            unitPrice: selectedProduct.unitPrice,
            tax: selectedProduct.tax,
            total: calculateItemTotal(item.quantity, selectedProduct.unitPrice, item.discount),
            availableQty: selectedProduct.quantity
          };
          return updatedItem;
        }
        return item;
      }));
    }
  };

  // Calculate total for a single item
  const calculateItemTotal = (quantity: number, unitPrice: number, discount: number) => {
    return quantity * unitPrice * (1 - discount / 100);
  };

  // Handle quantity change
  const handleQuantityChange = (itemId: string, quantity: number) => {
    setItems(items.map(item => {
      if (item.id === itemId) {
        const updatedItem = {
          ...item,
          quantity,
          total: calculateItemTotal(quantity, item.unitPrice, item.discount)
        };
        return updatedItem;
      }
      return item;
    }));
  };

  // Handle discount change
  const handleDiscountChange = (itemId: string, discount: number) => {
    setItems(items.map(item => {
      if (item.id === itemId) {
        const updatedItem = {
          ...item,
          discount,
          total: calculateItemTotal(item.quantity, item.unitPrice, discount)
        };
        return updatedItem;
      }
      return item;
    }));
  };

  // Remove an item
  const handleRemoveItem = (itemId: string) => {
    setItems(items.filter(item => item.id !== itemId));
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!customer.trim()) {
      toast({
        variant: "destructive",
        title: "Save Sale",
        description: "Please enter customer name"
      });
      return;
    }
    if (items.length === 0) {
      toast({
        variant: "destructive",
        title: "Save Sale",
        description: "Please add at least one item"
      });
      return;
    }
    if (items.some(item => !item.productId)) {
      toast({
        variant: "destructive",
        title: "Save Sale",
        description: "Please select a product for all items"
      });
      return;
    }
    setIsLoading(true);
    const newSale: Sale = {
      name: customer,
      soldAt: saleDate,
      items: items,
      total: grandTotal,
      paymentMode: paymentMode === "Cash" ? 1 : (paymentMode === "Card" ? 2 : 3),
      type: 2
    };

    const invoiceNumber = await saleService.createItem(newSale);
    if (invoiceNumber !== '') {
      setLastSaleData({
        customer,
        saleDate,
        items,
        grandTotal,
        paymentMode,
        invoiceNumber
      });

      setIsLoading(false);
      setSuccessDialogOpen(true);
    }
    else {
      setIsLoading(false);
      toast({
        variant: "destructive",
        title: "Save Sale",
        description: "Failed to save sale"
      });
    }
  }

  // Handle download bill
  const handleDownloadBill = () => {
    if (lastSaleData) {
      generateSaleBill({
        ...lastSaleData,
        paymentMode: lastSaleData.paymentMode
      });
    }
  };

  // Handle new sale
  const handleNewSale = () => {
    // Reset the form
    setCustomer("");
    setSaleDate(new Date().toISOString().split('T')[0]);
    setItems([]);
    setGrandTotal(0);
    setPaymentMode("Cash");
    setSuccessDialogOpen(false);
  };

  // Handle close and redirect to dashboard
  const handleClose = () => {
    setSuccessDialogOpen(false);
    navigate("/dashboard");
  };

  // Get payment mode icon
  const getPaymentModeIcon = (mode: PaymentMode) => {
    switch (mode) {
      case "Card":
        return <CreditCard size={16} className="mr-2" />;
      case "Cash":
        return <Banknote size={16} className="mr-2" />;
      case "UPI":
        return <Smartphone size={16} className="mr-2" />;
      default:
        return null;
    }
  };

  // Update grand total whenever items change
  useEffect(() => {
    const total = items.reduce((sum, item) => sum + item.total, 0);
    setGrandTotal(total);
  }, [items]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">New Sale</h1>
        </div>

        {/* Section 1: Header with customer name and date */}
        <Card>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="customer">Customer</Label>
                <Input
                  id="customer"
                  placeholder="Enter customer name"
                  value={customer}
                  onChange={(e) => setCustomer(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="date">Sale Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={saleDate}
                  onChange={(e) => setSaleDate(e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Add, Clear buttons */}
        <div className="flex justify-end gap-2">
          <Button onClick={handleAddItem}>
            <Plus size={16} className="mr-2" />
            Add Item
          </Button>
          <Button variant="outline" onClick={handleClearItems}>
            <Trash2 size={16} className="mr-2" />
            Clear All
          </Button>
        </div>

        {/* Section 3: Editable table */}
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[250px]">Product</TableHead>
                <TableHead>Quantity</TableHead>
                <TableHead>Unit Price</TableHead>
                <TableHead>Tax</TableHead>
                <TableHead>Discount</TableHead>
                <TableHead>Total</TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-6 text-muted-foreground">
                    No items added. Click "Add Item" to start.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="align-top">
                      <div>
                        <Select
                        value={item.productId.toString()}
                        onValueChange={(value) => handleProductChange(item.id, Number(value))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a product" />
                        </SelectTrigger>
                        <SelectContent>
                          {stocks.map(stock => (
                            <SelectItem key={stock.productId} value={stock.productId.toString()}>
                              {stock.productName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {item.productId > 0 && (
                           <div className="text-xs">
                             {item.availableQty === 0 ? (
                               <Alert variant="destructive" className="p-2">
                                 <AlertCircle className="h-4 w-4" />
                                 <AlertDescription>Out of stock</AlertDescription>
                               </Alert>
                             ) : (
                               <span className="text-muted-foreground">
                                 Available: {item.availableQty}
                               </span>
                             )}
                           </div>
                         )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => handleQuantityChange(item.id, parseInt(e.target.value) || 1)}
                        className="w-20"
                        disabled={item.availableQty === 0}
                      />
                    </TableCell>
                    <TableCell>
                      {item.unitPrice.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      {item.tax.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={item.discount}
                        onChange={(e) => handleDiscountChange(item.id, parseInt(e.target.value) || 0)}
                        className="w-20"
                      />
                    </TableCell>
                    <TableCell className="font-medium">
                      {item.total.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveItem(item.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Section 4: Payment mode, Submit button and grand total */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <div className="space-y-2 min-w-[200px]">
              <Select value={paymentMode} onValueChange={(value) => setPaymentMode(value as PaymentMode)}>
                <SelectTrigger id="paymentMode" className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cash">
                    <div className="flex items-center">
                      <Banknote size={16} className="mr-2" />
                      Cash
                    </div>
                  </SelectItem>
                  <SelectItem value="Card">
                    <div className="flex items-center">
                      <CreditCard size={16} className="mr-2" />
                      Card
                    </div>
                  </SelectItem>
                  <SelectItem value="UPI">
                    <div className="flex items-center">
                      <Smartphone size={16} className="mr-2" />
                      UPI
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={handleSubmit}
              disabled={items.length === 0 || !customer.trim() || isLoading}
              className="mt-6 sm:mt-0"
            >
              {isLoading ? (
                <>
                  <RefreshCw size={16} className="mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Save size={16} className="mr-2" />
                  Submit Sale
                </>
              )}
            </Button>
          </div>

          <div className="text-right">
            <p className="text-sm text-muted-foreground">Grand Total</p>
            <p className="text-2xl font-bold">{grandTotal.toFixed(2)}</p>
          </div>
        </div>

        {/* Success Dialog */}
        <Dialog open={successDialogOpen} onOpenChange={setSuccessDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-600">
                <CheckCircle className="h-6 w-6" />
                Sale Completed Successfully
              </DialogTitle>
              <DialogDescription>
                Your sale has been processed successfully. What would you like to do next?
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              <Alert className="bg-green-50 border-green-200 text-green-800">
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>
                  <div className="space-y-1">
                    <p>Sale of ${grandTotal.toFixed(2)} to {customer} has been recorded.</p>
                    <p className="flex items-center text-sm">
                      {getPaymentModeIcon(paymentMode)}
                      Payment Mode: {paymentMode}
                    </p>
                  </div>
                </AlertDescription>
              </Alert>
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={handleDownloadBill}
              >
                <Download size={16} className="mr-2" />
                Download Bill
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={handleNewSale}
              >
                <Plus size={16} className="mr-2" />
                New Sale
              </Button>
              <Button
                variant="default"
                className="flex-1"
                onClick={handleClose}
              >
                <Home size={16} className="mr-2" />
                Dashboard
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default SalePage;